import { useMemo, useState } from 'react';
import { SORT_STATE } from './ArrowsSortIcon';

/**
 * Hook that manages column sort state and returns sorted results.
 *
 * @param {Array} scanResults – raw result rows from the scan
 * @returns {{ sortStates, handleSort, sortedResults }}
 */
const useScanSort = (scanResults) => {
  const [sortStates, setSortStates] = useState({});

  const handleSort = (key) => {
    setSortStates((prev) => {
      const current = prev[key] || SORT_STATE.DEFAULT;
      let next;
      if (current === SORT_STATE.DEFAULT || current === SORT_STATE.DESC) {
        next = SORT_STATE.ASC;
      } else {
        next = SORT_STATE.DESC;
      }
      // Only one column sorted at a time
      return { [key]: next };
    });
  };

  const sortedResults = useMemo(() => {
    if (!scanResults || scanResults.length === 0) return scanResults;

    const sortEntry = Object.entries(sortStates).find(
      ([, state]) => state === SORT_STATE.ASC || state === SORT_STATE.DESC
    );
    if (!sortEntry) return scanResults;

    const [sortKey, sortDirection] = sortEntry;

    return [...scanResults].sort((a, b) => {
      let valA = a[sortKey];
      let valB = b[sortKey];

      // Handle object values (e.g. change with { absolute, percent })
      if (valA != null && typeof valA === 'object') {
        valA = valA.absolute ?? valA.abs ?? Object.values(valA)[0];
      }
      if (valB != null && typeof valB === 'object') {
        valB = valB.absolute ?? valB.abs ?? Object.values(valB)[0];
      }

      // Nulls/undefined always sort to the end
      if (valA == null && valB == null) return 0;
      if (valA == null) return 1;
      if (valB == null) return -1;

      // Numeric comparison
      const numA = typeof valA === 'number' ? valA : parseFloat(valA);
      const numB = typeof valB === 'number' ? valB : parseFloat(valB);

      if (!isNaN(numA) && !isNaN(numB)) {
        return sortDirection === SORT_STATE.ASC ? numA - numB : numB - numA;
      }

      // String comparison
      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();
      const cmp = strA.localeCompare(strB);
      return sortDirection === SORT_STATE.ASC ? cmp : -cmp;
    });
  }, [scanResults, sortStates]);

  return { sortStates, handleSort, sortedResults };
};

export default useScanSort;
